<template>
  <div class="rbz-card-collapsible" :class="{ collapsed: isCollapsed }">
    <div
      class="rbz-card-header"
      data-qa="card-header"
      @click="toggleCollapsed($event)"
      @keypress.space.prevent
      @keyup.space="toggleCollapsed($event)"
      @keydown.enter="toggleCollapsed($event)"
      @keydown.up.prevent.stop="setCollapsed(true)"
      @keydown.down.prevent.stop="setCollapsed(false)"
      tabindex="0"
    >
      <div class="rbz-card-title rbz-text-large ellipsis">
        <slot name="title" :isCollapsed="isCollapsed" />
      </div>
      <icon-chevron
        color="dusty-gray"
        data-qa="card-chevron"
        :direction="isCollapsed ? 'down' : 'up'"
      />
    </div>
    <div class="rbz-card-content" data-qa="card-content" v-show="!isCollapsed">
      <slot name="content" :isCollapsed="isCollapsed" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import IconChevron from '../../assets/icons/IconChevron.vue';

interface RbzCardCollapsibleProps {
  isCollapsed?: boolean;
}

const props = withDefaults(defineProps<RbzCardCollapsibleProps>(), {
  isCollapsed: true,
});

interface RbzCardCollapsibleEmits {
  (e: 'update:isCollapsed', value: boolean): void;
}

const emit = defineEmits<RbzCardCollapsibleEmits>();

const isCollapsed = ref(props.isCollapsed);

const setCollapsed = (newValue: boolean) => {
  if (isCollapsed.value !== newValue) {
    isCollapsed.value = newValue;
    emit('update:isCollapsed', isCollapsed.value);
  }
};

const toggleCollapsed = (event: MouseEvent | KeyboardEvent) => {
  if (!event.originatedFromButton && !event.elementsClicked?.includes('menuListItem')) {
    isCollapsed.value = !isCollapsed.value;
    emit('update:isCollapsed', isCollapsed.value);
  }
};
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '../../assets/styles/colors' as colors;

.rbz-card-collapsible {
  background: map.get(colors.$colors, 'white');
  border-radius: 4px;
  box-shadow: 1px 0 8px -1px rgba(map.get(colors.$colors, 'black'), 0.12);
  display: flex;
  flex-direction: column;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: 24px;
  }
}

.rbz-card-collapsible.collapsed {
  .rbz-card-header {
    border-radius: 4px;
  }
}

.rbz-card-header {
  align-items: flex-start;
  background: map.get(colors.$colors, 'white');
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding: 12px 16px 12px 24px;

  &:hover {
    background: map.get(colors.$colors, 'selago');
  }

  &:active {
    background: map.get(colors.$colors, 'hawkes-blue');
  }

  &:focus:not(:active) {
    border: 2px solid map.get(colors.$colors, 'malibu');
    outline: none;
    padding: 10px 14px 10px 22px;
  }

  &.disabled {
    pointer-events: none;

    .rbz-card-title {
      color: map.get(colors.$colors, 'silver');
    }
  }
}

.rbz-card-title {
  color: map.get(colors.$colors, 'bright-gray');
  display: flex;
  flex-grow: 1;
  gap: 4px;
  overflow: visible;
}

.rbz-card-content {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: 16px 16px 16px 24px;
  position: relative;
}
</style>
